import type { Redesign18060Subject } from './onboarding.redesign18060'

export type QuestionnairePage =
  | TextPage
  | OptionsPage
  | DynamicOptions18060Page
  | FinishingSetupPage
  | ReviewPage
  | StatementsPage
  | BooksPage
  | Books18060Page
  | DailyGoalPage
  | NotificationsPage
  | CommitmentPactPage
  | WelcomePage
  | SplitscreenPage
  | UpgradePage
  | GenreSelectionPage
  | BookSelectionPage
  | LoadingPage
  | SummaryPage
  | MillionUsersPage
  | ProductDemoPage

export type QuestionnairePageType = QuestionnairePage['type']

export interface BaseQuestionnairePage {
  type: QuestionnairePageType
  id: string
  title: string
  variableTitle?: string
  subtitle?: string
  image?: QuestionnaireImage
  animation?: QuestionnaireAnimation
  content?: any[]
  hideContinueButton?: boolean
  showLogo?: boolean
}

export interface TextPage extends BaseQuestionnairePage {
  type: 'Text'
  imageAnimation?: 'slide-up' | 'wave'
  textAlignment?: 'left' | 'center'
  textSize?: 'normal' | 'large'
}

export interface OptionsPage extends BaseQuestionnairePage {
  type: 'Options'
  options: QuestionnaireFormOption[]
  multipleOptions?: boolean
  maxOptions?: number
  wrapInputs?: boolean
  centerText?: boolean
}

export interface DynamicOptions18060Page extends BaseQuestionnairePage {
  type: 'DynamicOptions18060'
  sourcePageId: string
  defaultSourceSubject: Redesign18060Subject
  optionsBySubject: Record<Redesign18060Subject, readonly string[]>
}

export interface FinishingSetupPage extends BaseQuestionnairePage {
  type: 'FinishingSetup'
  titleLoading: string
  hideContinueButton: true
  steps: FinishingSetupPageSteps[]
}

export interface ReviewPage extends BaseQuestionnairePage {
  type: 'Review'
  review: string
  reviewer: string
}

export interface StatementsPage extends BaseQuestionnairePage {
  type: 'Statements'
  hideContinueButton: true
  content: string[]
}

export interface BooksPage extends BaseQuestionnairePage {
  type: 'Books'
  content: string[]
}

export interface Books18060Page extends BaseQuestionnairePage {
  type: 'Books18060'
  content: string[]
  hideContinueButton: true
}

export interface DailyGoalPage extends BaseQuestionnairePage {
  type: 'DailyGoal'
}

export interface NotificationsPage extends BaseQuestionnairePage {
  type: 'Notifications'
  options: QuestionnaireNotificationsOption[]
}

export interface CommitmentPactPage extends BaseQuestionnairePage {
  type: 'CommitmentPact'
  textInitial: string
  textFinal: string
}

export interface WelcomePage extends BaseQuestionnairePage {
  type: 'Welcome'
  introTitle: string
  image: QuestionnaireImage
  hideContinueButton: true
}

export interface SplitscreenPage extends BaseQuestionnairePage {
  type: 'Splitscreen'
  topText: string[]
  bottomText: string[]
  animation: QuestionnaireAnimation
  hideContinueButton: boolean
}

export interface UpgradePage extends BaseQuestionnairePage {
  type: 'Upgrade'
  sectionTitles: string[]
  sectionSubtitles: string[]
  animation: QuestionnaireAnimation
}

export interface GenreSelectionPage extends BaseQuestionnairePage {
  type: 'GenreSelection'
  options: {
    id: QuestionnaireGenre
    icon: string
    label: string
    color: string
  }[]
}

export interface BookSelectionPage extends BaseQuestionnairePage {
  type: 'BookSelection'
  hideContinueButton: true
}

export interface LoadingPage extends BaseQuestionnairePage {
  type: 'Loading'
  titleDone: string
  titleError: string
  animation: QuestionnaireAnimation
  hideContinueButton: boolean
}

export interface SummaryPage extends BaseQuestionnairePage {
  type: 'Summary'
}

export interface MillionUsersPage extends BaseQuestionnairePage {
  type: 'MillionUsers'
  content: {
    name: string
    subtitle: string
    image: string
    text: string
  }[]
  animation: QuestionnaireAnimation
  hideContinueButton: true
}

export interface ProductDemoPage extends BaseQuestionnairePage {
  type: 'ProductDemo'
  content: string[]
  animationMobile: QuestionnaireAnimation
  animationDesktop: QuestionnaireAnimation
  hideContinueButton?: boolean
}

export const QUESTIONNAIRE_GENRES = [
  'communication',
  'relationships',
  'productivity',
  'business',
  'leadership',
  'health',
] as const

export type QuestionnaireGenre = (typeof QUESTIONNAIRE_GENRES)[number]

export interface QuestionnaireBook {
  id: string
  title: string
  cover: string
  popularity: number
  genre: QuestionnaireGenre[]
}

export type QuestionnaireChoice =
  | OptionsChoice
  | StatementsChoice
  | FinishingSetupChoice
  | NotificationsChoice
  | DailyGoalChoice
  | BooksChoice
  | GenreSelectionChoice
  | BookSelectionChoice

export type OptionsChoice = string | string[]
export type StatementsChoice = string[]
export type FinishingSetupChoice = string[]
export type NotificationsChoice = string[]
export type DailyGoalChoice = number
export type BooksChoice = {
  id: string
  score: number
}[]
export type GenreSelectionChoice = QuestionnaireGenre[]
export type BookSelectionChoice = {
  id: string
  title: string
  cover: string
}[]

export type TrialStep = {
  text: string
  icon: string
  day: string
}

export type QuestionnaireUserChoices = Record<string, QuestionnaireChoice>

export interface QuestionnaireImage {
  src: string
  mobileSrc?: string
  width: number
  height: number
}

export interface QuestionnaireAnimation extends QuestionnaireImage {
  stateMachines?: string | string[]
}

export interface QuestionnaireFormOption {
  label: string
  icon?: string
  type?: 'text-input' | 'skip'
  placeholder?: string
  hint?: string
}

export interface FinishingSetupPageSteps {
  title: string
  titleLoading: string
  question: string
}

export interface QuestionnaireNotificationsOption {
  title: string
  subtitle: string
}

export interface QuestionnaireSection {
  id: string
  title: string
  pages: QuestionnairePage[]
}

export interface OnboardingIntroPage {
  title: string
  image: string
  subtitle?: string
}

export interface OnboardingTrialPage {
  title: string
  image?: QuestionnaireImage
  steps?: TrialStep[]
}

export const OFFER_GUIDE_KEY = 'sf_17817_offer_guide'
